import React, { useEffect } from "react";
import { X } from "lucide-react";
import { ImageAttachment } from "../types";
import { apiService } from "../services/api";

interface ImageModalProps {
  image: ImageAttachment;
  threadId: string;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ image, threadId, onClose }) => {
  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // Get image URL
  const imageUrl = apiService.getImageUrl(threadId, image.filename);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 dark:bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative max-w-7xl max-h-[90vh] w-full mx-4 flex flex-col">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 z-10 p-2 text-white hover:text-neutral-300 transition-colors rounded-lg hover:bg-white/10"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {/* Image container */}
        <div className="relative flex-1 flex items-center justify-center bg-neutral-900 rounded-lg overflow-hidden">
          <img
            src={imageUrl}
            alt={image.filename}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {/* Image info */}
        <div className="mt-4 px-4 py-3 bg-white dark:bg-neutral-800 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {image.filename}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {formatFileSize(image.size)} •{" "}
                {new Date(image.uploaded_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
